import React from 'react';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { AppBar, Button, Card, CardActions, CardContent, CardMedia, CircularProgress, Toolbar, Typography } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { NavigatorEntry } from './NavigatorData';
import { Link } from 'react-router-dom';
import { LicenseRequestData, LicenseRequestStatus } from './Paperbase';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { DTFormatter } from '../general/datetime-formatter/DTFormatter';
import { DownloadInfo, Platform, ProductsDataSet, findProductData, findServerData, initialiseDownload} from './DownloadProductData';


const styles = (theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 1046,
      margin: 'auto',
      overflow: 'hidden',
    },
    subPaper: {
      margin: 'auto',
      maxWidth: 946,
      marginBottom: 20,
      paddingTop: 15,
      paddingBottom: 15,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    searchBar: {
      borderBottom: '1px solid ' + MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND
    },
    subSearchBar: {
      margin: 'auto',
      maxWidth: 946,
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      color: DeltaXMLColors.CELERY,
      height: 45,
      //marginTop: 25,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
    },
    contentWrapper: {
      margin: '40px 10px',
    },
    noProductContentWrapper: {
      margin: '40px 10px',
      justifyContent: 'center',
      display: 'flex'
    },
    button: {
      float: 'right',
      margin: 2,
    },
    buttonRefresh: {
      float: 'right',
    },
    progress: {
      float: 'right',
      margin: '2px 8px 0px 0px',
      color: MyDeltaColors.WHITE
    },
    detailsImage: {
      display: 'flex',
      flexDirection: 'row',
      height: 150,
      width: 130,
      alignItems: 'left',
      justifyContent: 'left',
    },
    detailsCard: {
      display: 'flex',
      flexDirection: 'row',
      height: 110,
      width: 320,
      alignItems: 'left',
      justifyContent: 'left',
    },
    root: {
      maxWidth: 500,
      alignItems: 'center',
    },
    cardRoot: {
      display: 'flex',
      height: 150,
      width: 450,
      margin: 5,
    },
    content: {
      flex: '1 0 auto',
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    cardImageCover: {
      height: 150,
      width: 130,
      backgroundColor: DeltaXMLColors.OXFORD_BLUE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      verticalAlign: 'middle',
    },
    cardRootContent: {
      display: 'flex',
      alignItems: 'left',
      justifyContent: 'left',
      flexDirection: 'row',
      backgroundColor: DeltaXMLColors.CONCRETE,
      position: 'relative',
    },
    downlaodPageCardButtons: {
      bottom: 9,
      height: '35px',
      marginLeft: '2px'
    },
    productDetails: {
      color: DeltaXMLColors.OXFORD_BLUE,
      backgroundColor: 'transparent'
    }
  });

declare global {
  interface Window { ATL_JQ_PAGE_PROPS: any; }
}

window.ATL_JQ_PAGE_PROPS = window.ATL_JQ_PAGE_PROPS || {};

export interface DownloadProductProps extends WithStyles<typeof styles> {
  handleRefresh: () => void;
  responseState: LicenseRequestData;
  downloadProductData?: ProductsDataSet;
  downloadSections: NavigatorEntry[];
  downloadServerSections: NavigatorEntry[];
  dateFormat: string;
}

function DownloadProduct(props: DownloadProductProps) {
  const { classes, handleRefresh, responseState, downloadProductData, downloadServerSections, downloadSections, dateFormat } = props;

  let productDownload: DownloadInfo = initialiseDownload;
  let productDownloadDotNet: DownloadInfo = initialiseDownload;

  let serverDownload: DownloadInfo = initialiseDownload;

  let downloadHeaderText = 'My Downloads'
  let msg = 'products data not yet requested';
  let downloadsFetchError = false;
  // error message:
  const { downloads } = responseState;
  if (downloads.status === LicenseRequestStatus.notYetRequested) {
    msg = 'Authenticating...';
  } if (downloads.status === LicenseRequestStatus.errorResponse) {
    downloadsFetchError = true;
    msg = downloads.text ? `${downloads.text}` : 'Error encountered when requesting downloads';
  } else if (downloads.status === LicenseRequestStatus.inProgress) {
    msg = 'Requesting downloads...';
  } else {
    msg = '';
  }

  const downloadsAvailable = downloads.status !== LicenseRequestStatus.noItems;
  msg = msg === '' ? msg : '[' + msg + ']';
  const showProgressBar = downloads.status === LicenseRequestStatus.inProgress;

  const findCurrentProduct = (productId: string) => {
    [productDownload, productDownloadDotNet] = findProductData(productId, downloadProductData!);
  }

  const findServer = (serverid: string) => {
    [serverDownload] = findServerData(serverid, downloadProductData!);
  }

  const getDownloadURL = (productId: string, platform?: Platform) => {
    platform ? findCurrentProduct(productId): findServer(productId);
    window.location.href = platform === Platform.JAVA ? productDownload.download_url : 
                           platform === Platform.DOTNET ? productDownloadDotNet.download_url :
                           serverDownload.download_url;
  }

  return (
    <Paper className={classes.paper}>
      <AppBar className={classes.searchBar} position="static" color="default" elevation={0}>
        <Toolbar>
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="h4">
                {downloadHeaderText}
              </Typography>
            </Grid>
            {(downloadsAvailable || downloadsFetchError) &&
              <React.Fragment>
                <Grid item xs={5}>
                  <Typography variant="body2" color="textSecondary" style={{ color: "white" }}>
                    {msg}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  {
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.buttonRefresh}
                      onClick={handleRefresh}>
                      Refresh
                    </Button>
                  }
                  {
                    showProgressBar &&
                    <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
                  }
                </Grid>
              </React.Fragment>}
          </Grid>
        </Toolbar>
      </AppBar>
      <div className={classes.contentWrapper}>
        {downloadSections && downloadSections.length > 0 &&
          <div>
            <AppBar className={classes.subSearchBar} position="static" color="default" elevation={0}>
              <Toolbar>
                <Typography variant="h5">Solution Downloads</Typography>
              </Toolbar>
            </AppBar>
            <Card className={classes.subPaper}>
              <Grid container spacing={2} direction="row" alignItems="center" justify="center">
                {downloadSections.map(({ id: productId, link: productRefLink, icon: productIcon, productInfo: productInfo }) => {
                  findCurrentProduct(productId);
                  return (productDownload.download_url.length !== 0 &&
                    <Card key={productId} className={classes.cardRoot}>
                      <div className={classes.cardRootContent}>
                        <Link to={productRefLink} style={{ textDecoration: 'none' }} className={classes.detailsImage}>
                          <div className={classes.cardImageCover}>
                            <CardMedia
                              component="img"
                              image={productIcon.props.src}
                              title={productId}
                            />
                          </div>
                        </Link>
                        <div>
                          <Link to={productRefLink} style={{ textDecoration: 'none' }} className={classes.detailsCard}>
                            <CardContent className={classes.content}>
                              <Typography variant="h6" style={{ color: DeltaXMLColors.OXFORD_BLUE }}>
                                <b>{productId}</b>
                              </Typography>
                              <Typography variant="body2" style={{ paddingTop: '10px' }} className={classes.productDetails}>
                                <b>Release:</b>&nbsp;{productDownload.version}
                              </Typography>
                              <Typography variant="body2" className={classes.productDetails}>
                                <b>Date:</b>&nbsp;{DTFormatter.getFormattedDate(productDownload.release_date, dateFormat)}
                              </Typography>
                            </CardContent>
                          </Link>
                          <div className={classes.controls}>
                            <Button variant="contained" color="primary" size="small" onClick={() => getDownloadURL(productId, Platform.JAVA)} className={classes.downlaodPageCardButtons}>
                              <GetAppIcon />&nbsp;Download
                            </Button>
                            <Link to={productRefLink} style={{ textDecoration: 'none' }}>
                              <Button variant="contained" color="primary" size="small" className={classes.downlaodPageCardButtons}>Previous Releases</Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </Grid>
            </Card>
            <AppBar className={classes.subSearchBar} position="static" color="default" elevation={0}>
              <Toolbar>
                <Typography variant="h5">License Downloads</Typography>
              </Toolbar>
            </AppBar>
            <Card className={classes.subPaper}>
              <Grid container spacing={2} direction="row" alignItems="center" justify="center">
                {downloadServerSections && downloadServerSections.map(({ id: productId, link: productRefLink, icon: productIcon, productInfo: productInfo }) => {
                  findServer(productId)
                  return (serverDownload.download_url.length !== 0 &&
                    <Card key={productId} className={classes.cardRoot}>
                      <div className={classes.cardRootContent}>
                        <Link to={productRefLink} style={{ textDecoration: 'none' }} className={classes.detailsImage}>
                          <div className={classes.cardImageCover}>
                            <CardMedia
                              component="img"
                              image="/DeltaXignia_LogoMark_Colour.svg"
                              title="License Server"
                            />
                          </div>
                        </Link>
                        <div>
                          <Link to={productRefLink} style={{ textDecoration: 'none' }} className={classes.detailsCard}>
                            <CardContent className={classes.content}>
                              <Typography variant="h6" style={{ color: DeltaXMLColors.OXFORD_BLUE }}>
                                <b>{productId}</b>
                              </Typography>
                              <Typography variant="body2" style={{ paddingTop: '10px' }} className={classes.productDetails}>
                                <b>Release:</b>&nbsp;{serverDownload.version}
                              </Typography>
                              <Typography variant="body2" className={classes.productDetails}>
                                <b>Date:</b>&nbsp;{DTFormatter.getFormattedDate(serverDownload.release_date, dateFormat)}
                              </Typography>
                            </CardContent>
                          </Link>
                          <div className={classes.controls}>
                            <Button variant="contained" color="primary" size="small" className={classes.downlaodPageCardButtons} onClick={() => getDownloadURL(productId)}>
                              <GetAppIcon />&nbsp;Download
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </Grid>
            </Card>
          </div>
        }
        {(!downloadsAvailable) ? <div className={classes.noProductContentWrapper}>
          <Card className={classes.root}>
            <CardContent>
              <Typography variant="body1" color="textSecondary">You currently have no purchased licenses to download our DeltaXignia solution.
                To see the fantastic solution we have to offer and where you can purchase them, please click on the button below.</Typography>
            </CardContent>
            <CardActions>
              <div style={{ width: '100%' }}>
                <Button className={classes.button} variant="contained" color="primary" onClick={() => window.open('https://www.deltaxml.com/products', '_blank')}>
                  Explore Available Solution
                </Button>
              </div>
            </CardActions>
          </Card>
        </div> : <></>}
      </div>
    </Paper>);
}

export default withStyles(styles)(DownloadProduct);
